import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableHeaderFilter } from "@lib/types/Api";
import { useTypedSelector } from "@lib/redux";
import { SharedContextProps } from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import { apiGetAILogs } from "@lib/api/aiLog";
import { AILog } from "@lib/types/AILog";
import { AILogDrawer } from "@lib/components/PageComponents/AILog";

const SharedAILogs: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  customTitle,
  pageIsActive = true,
  withoutContainer = false,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { user } = useTypedSelector((state) => state.auth);

  const [selectedAILog, setSelectedAILog] = useState<AILog | null>(null);
  const handleSelect = (item: AILog) => {
    setSelectedAILog(item);
  };

  const initialFilters = [];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    filter: initialFilters,
    join: ["customer", "walletAddress"],
  });

  const aiLogsQuery = useQuery({
    queryFn: () => apiGetAILogs(params),
    queryKey: ["ai-log", JSON.stringify(params)],
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching } = aiLogsQuery;
  const apiData = data?.data?.data;

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<AILog>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        navigate,
        apiData,
        params,
        messages,
        userRole: user?.role,
        handleSelect,
      }),
    [apiData, params, user?.role, handleSelect],
  );

  const hiddenFilters = useMemo(() => {
    const filterNames = [];
    return filterNames;
  }, [detailContext, context]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params]);

  const title = customTitle || (messages["common.aiLogs"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={!!withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={aiLogsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          onChange={onChange}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          size="small"
          minusHeight={minusHeight}
        />
      </AppsContent>

      <AILogDrawer
        open={!!selectedAILog}
        onClose={() => setSelectedAILog(null)}
        item={selectedAILog}
      />
    </AppsContainer>
  );
};

export default SharedAILogs;
